import api from "./api";

export interface BillingPlan {
  id: number;
  name: string;
  plan_code: string | null;
  monthly_price: number;
  annual_price: number;
  recommended: boolean;
  limits: {
    employees: number;
    services: number;
    deals: number;
    roles: number;
  };
  modules: string[];
  stripe: {
    monthly_plan_id: string | null;
    annual_plan_id: string | null;
  };
}

export interface BillingPaymentMethod {
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
}

export interface BillingSubscription {
  id: number;
  status: string;
  stripe_status: string | null;
  interval: string;
  amount_cents: number;
  currency: string;
  next_charge_at: string | null;
  package_id: number | null;
  package_name: string | null;
}

export interface BillingOverview {
  company_id: number;
  subscription: BillingSubscription | null;
  plan: {
    id: number;
    name: string;
    monthly_price: number;
    annual_price: number;
  } | null;
  billing_cycle: string | null;
  subscription_status: string | null;
  payment_method: BillingPaymentMethod | null;
}

export interface BillingHistoryItem {
  id: number;
  event: string;
  label: string;
  status: string | null;
  billing_cycle: string | null;
  amount_cents: number | null;
  currency: string;
  happened_at: string | null;
  error: string | null;
}

export interface BillingHistoryResponse {
  items: BillingHistoryItem[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

function unwrapData<T>(response: { data?: { data?: T; status?: string; message?: string } }): T {
  if (response.data?.status === "error") {
    throw new Error(response.data?.message || "Request failed.");
  }
  const payload = response.data?.data;
  if (payload == null) {
    throw new Error("No data returned from billing API.");
  }
  return payload as T;
}

export const getPlans = async (): Promise<BillingPlan[]> => {
  const response = await api.get("/plans");
  return response.data?.data ?? [];
};

export const createStripeSetupIntent = async (): Promise<{
  client_secret: string;
  setup_intent_id: string;
}> => {
  const response = await api.post("/admin/billing/stripe/setup-intent");
  return response.data?.data;
};

export const subscribeToPlan = async (payload: {
  plan_id: number;
  type: "monthly" | "annual";
  payment_method: string;
  stripe_email?: string;
}) => {
  const response = await api.post("/admin/billing/stripe/subscribe", payload);
  return response.data?.data;
};

export const getBillingOverview = async (): Promise<BillingOverview> => {
  const response = await api.get("/admin/billing/overview");
  return unwrapData<BillingOverview>(response);
};

export const getBillingHistory = async (
  page = 1,
  perPage = 12,
): Promise<BillingHistoryResponse> => {
  const response = await api.get("/admin/billing/history", {
    params: { page, per_page: perPage },
  });
  return unwrapData<BillingHistoryResponse>(response);
};

export function formatBillingAmount(amountCents: number | null | undefined, currency = "gbp"): string {
  const amount = Number(amountCents ?? 0) / 100;
  const code = currency.toUpperCase();
  if (code === "GBP") return `£${amount.toFixed(2)}`;
  if (code === "USD") return `$${amount.toFixed(2)}`;
  if (code === "EUR") return `€${amount.toFixed(2)}`;
  return `${amount.toFixed(2)} ${code}`;
}

export function formatCardBrand(brand?: string | null): string {
  if (!brand) return "Card";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

export function formatBillingCycle(cycle?: string | null): string {
  const raw = String(cycle || "monthly").toLowerCase();
  if (raw === "annual" || raw === "yearly" || raw === "year") return "Yearly";
  return "Monthly";
}

export function formatSubscriptionStatus(status?: string | null): string {
  const raw = String(status || "unknown").toLowerCase();
  if (raw === "active") return "Active";
  if (raw === "past_due") return "Past due";
  if (raw === "pending_setup" || raw === "pending") return "Pending setup";
  if (raw === "cancelled" || raw === "canceled") return "Cancelled";
  return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
